<template>
  <div
    :class="[
      $style.container,
      {
        [$style['side-by-side']]: mobileSideBySide,
        [$style.hasExclusiveOffer]: showExclusiveOfferMessage
      }
    ]"
  >
    <template v-if="showExclusiveOfferMessage">
      <div :class="$style.exclusiveMessage">
        <p>{{ exclusiveOfferMessage }}</p>
        <button
          :class="{ short: !mobileSideBySide }"
          class="primary-outline fluid"
          @click.stop="goToDetails"
        >
          {{ translations.seeDetails }}
        </button>
      </div>
    </template>
    <template v-else>
      <div
        v-if="statusMessage"
        :class="$style.statusMessage"
        data-qa="status-message"
        :data-qa-value="statusMessageKey"
        v-html="statusMessage"
      />
      <NsProductQuantitySelector
        :class="$style.quantitySelector"
        :selected="quantity"
        :disabled="disableQuantitySelector"
        :full-width="mobileSideBySide"
        :max-available="maxQuantity"
        :size="mobileSideBySide ? 'small' : undefined"
        :translations="translations"
        inline-label
        @select="handleQuantitySelect"
      />
      <button
        v-if="!showAdrOnly"
        :class="{ short: !mobileSideBySide }"
        class="primary-solid fluid"
        :disabled="disableAddToCart"
        data-testid="addToCart"
        @click.stop="$emit('add-to-cart', $event)"
      >
        <NsIcon
          v-if="!mobileSideBySide"
          :class="$style.addToCartIcon"
          icon-name="icon-smallcart-empty"
        />
        {{ translations.addToCart }}
      </button>
      <button
        v-else
        :class="{ short: !mobileSideBySide }"
        class="primary-solid fluid"
        :disabled="disableAddToAdr"
        data-testid="addToAdr"
        @click.stop="$emit('add-to-adr')"
      >
        <NsIcon
          v-if="!mobileSideBySide"
          :class="$style.addToCartIcon"
          icon-name="icon-refresh"
        />
        {{ translations.addToAdr }}
      </button>
    </template>
  </div>
</template>

<script>
import { NsIcon } from "@nuskin/design-components";
import NsProductQuantitySelector from "./NsProductQuantitySelector.vue";
import "@nuskin/ns-core-styles/src/dist/main.css";

export default {
  name: "NsProductCardAction",
  components: { NsIcon, NsProductQuantitySelector },
  props: {
    /**
     * Disables the Add To Adr Button
     */
    disableAddToAdr: Boolean,
    /**
     * Disables the Add To Cart Button
     */
    disableAddToCart: Boolean,
    /**
     * Disables the Quantity Selector
     */
    disableQuantitySelector: Boolean,
    /**
     * The Exclusive Offer text
     */
    exclusiveOfferMessage: {
      type: String,
      default: ""
    },
    /**
     * Sets the max quantity for the Quantity Selector
     */
    maxQuantity: {
      type: Number,
      default: null
    },
    /**
     * Determines if the product action should have the small-width styling to support side-by-side in mobile
     */
    mobileSideBySide: Boolean,
    /**
     * Sets the quantity for the Quantity Selector
     */
    quantity: {
      type: String,
      default: "1"
    },
    /**
     * Whether to show the exclusive offer message
     */
    showExclusiveOfferMessage: Boolean,
    /**
     * Warning message, if product is out of stock or whatever
     */
    statusMessage: {
      type: String,
      default: ""
    },
    /**
     * Warning message key, for qa testing automation
     */
    statusMessageKey: {
      type: String,
      default: ""
    },
    /**
     * Translations
     */
    translations: {
      type: Object,
      required: true
    }
  },
  computed: {
    showAdrOnly() {
      return this.disableAddToCart && !this.disableAddToAdr;
    }
  },
  methods: {
    handleQuantitySelect(value, index) {
      this.$emit("quantity-select", value, index);
    },
    goToDetails() {
      this.$emit("go-to-details");
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typography";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/buttons";

.container,
.container * {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

.container {
  position: relative;
  width: 100%;
  display: grid;
  grid-gap: 10px;
  background-color: white;
  padding: 0 20px 20px;

  &.hasExclusiveOffer {
    padding: 0;
  }

  button {
    // margin override for backOfficeDashboard.clientlibs.css
    margin: 0;
  }
}

.exclusiveMessage {
  display: grid;
  grid-gap: 10px;
  justify-content: center;
  background-color: #f9f0f9; // map.get(colors.$baseColors, ariel);
  padding: 10px 20px 20px;
}

.statusMessage {
  font-weight: map.get(typographyVariables.$typography, font-weight-normal);
  color: map.get(colors.$baseColors, color-error);
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.quantitySelector {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.addToCartIcon {
  position: relative;
  top: 1px;
  stroke-width: 1.5px;
}

@media (max-width: 767px) {
  .container.side-by-side {
    padding: 0 12px 20px;
    grid-gap: 12px;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 1) 40.5px,
      map.get(colors.$themeColors, color-background-1) 40.5px
    );

    &.hasExclusiveOffer {
      padding: 0;
    }

    .exclusiveMessage {
      grid-gap: 12px;
      padding: 12px;
      padding-top: 0;
      background-color: unset;
      font-size: 14px;
      text-align: left;
      overflow: hidden;
      background: linear-gradient(
        to top,
        rgba(255, 255, 255, 1) 34.5px,
        #f9f0f9 34.5px
      );

      p {
        margin: 0;
        font-size: 14px;
        line-height: 22px;
      }
    }

    .statusMessage {
      margin: 0;
      font-size: 14px;
      line-height: 22px;
      text-align: left;
    }
  }
}
</style>
